import type { ActionType, ProTableProps } from "@ant-design/pro-components";
import { ProTable } from "@ant-design/pro-components";
import { cloneElement, isValidElement, useRef } from "react";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";

// 默认搜索表单配置
const defaultSearchConfig = {
  labelWidth: "auto" as const,
  collapsed: false,
  collapseRender: false,
  span: 8,
  optionRender: (searchConfig, formProps, dom) => {
    const [submitBtn, resetBtn] = dom;
    return [
      isValidElement(resetBtn)
        ? cloneElement(resetBtn as unknown as React.ReactElement<any>, {
            icon: <SearchOutlined />,
          })
        : resetBtn,
      isValidElement(submitBtn)
        ? cloneElement(submitBtn as unknown as React.ReactElement<any>, {
            icon: <ReloadOutlined />,
          })
        : submitBtn,
    ];
  },
} satisfies ProTableProps<any, any, any>["search"];

// 默认分页配置
const defaultPaginationConfig = {
  defaultPageSize: 10,
  showSizeChanger: true,
  showTotal: (total: number) => `共 ${total} 条`,
};

// 默认滚动配置
const defaultScrollConfig = {
  x: "max-content" as const,
};

// 默认工具栏配置
const defaultToolbarConfig = {
  settings: [],
} satisfies ProTableProps<any, any, any>["toolbar"];

/**
 * 通用表格组件
 * 完全继承 ProTable 的所有类型和功能，提供合理的默认配置
 */
function BaseProtable<
  T extends Record<string, any>,
  U extends Record<string, any> = Record<string, any>,
  ValueType = "text",
>(props: ProTableProps<T, U, ValueType>) {
  const {
    search = defaultSearchConfig,
    pagination = defaultPaginationConfig,
    scroll,
    toolbar = defaultToolbarConfig,
    rowKey = "id",
    actionRef: externalActionRef,
    ...restProps
  } = props;

  const internalActionRef = useRef<ActionType>();
  const actionRef = externalActionRef || internalActionRef;

  // 合并 scroll 配置，允许外部覆盖默认值
  const mergedScroll = {
    ...defaultScrollConfig,
    ...scroll,
  };

  return (
    <ProTable<T, U, ValueType>
      actionRef={actionRef}
      rowKey={rowKey}
      search={search}
      pagination={pagination}
      scroll={mergedScroll}
      toolbar={toolbar}
      bordered
      tableAlertRender={false}
      {...restProps}
    />
  );
}

export default BaseProtable;
